'use client';

import type { Answers } from '@insta-club/models';
import { cn } from '@insta-club/utilities';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronIcon } from 'src/libs/components/icons';
import ChatBubble from './chatBubble';
import ChipDate from './chipDate';
import TicketDescription from './ticketDescription';

interface IProps {
    groups: Answers[];
    className?: string;
    makeScrollable?: boolean;
    bottomThresholdPx?: number;
    autoScrollOnNew?: 'always' | 'near-bottom';
}

const TicketThread = ({
    groups,
    className,
    makeScrollable = true,
    bottomThresholdPx = 48,
    autoScrollOnNew = 'always'
}: IProps) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLDivElement | null>(null);

    const [showGoDown, setShowGoDown] = useState(false);

    const hasMessages = useMemo(() => Boolean(groups?.length && groups.some((g) => g.items?.length)), [groups]);

    const scrollToBottom = useCallback((smooth = true) => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({
                behavior: smooth ? 'smooth' : 'auto',
                block: 'nearest'
            });
            return;
        }
        const el = wrapperRef.current;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    }, []);

    const isNearBottom = useCallback(() => {
        const el = wrapperRef.current;
        if (!el) return true;
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        return distanceFromBottom <= bottomThresholdPx;
    }, [bottomThresholdPx]);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;

        const onScroll = () => setShowGoDown(!isNearBottom());
        onScroll();

        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, [isNearBottom]);

    useLayoutEffect(() => {
        if (!hasMessages) return;
        scrollToBottom(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMessages]);

    const lastMessageKey = useMemo(() => {
        const lastGroup = groups?.[groups.length - 1];
        const lastItem = lastGroup?.items?.[lastGroup.items.length - 1];
        return lastItem
            ? `${lastGroup?.date?.year}-${lastGroup?.date?.month}-${lastGroup?.date?.day}-${lastItem.id}`
            : '';
    }, [groups]);

    useEffect(() => {
        if (!hasMessages) return;

        const run = () => {
            if (autoScrollOnNew === 'always') {
                scrollToBottom(true);
            } else {
                if (isNearBottom()) scrollToBottom(true);
            }
        };

        const id = window.setTimeout(run, 0);
        return () => window.clearTimeout(id);
    }, [lastMessageKey, hasMessages, autoScrollOnNew, isNearBottom, scrollToBottom]);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;

        const onLoad = (e: Event) => {
            const target = e.target as HTMLElement | null;
            if (target && target.tagName === 'IMG') {
                if (autoScrollOnNew === 'always' || isNearBottom()) {
                    // scrollToBottom(true);
                }
            }
        };

        el.addEventListener('load', onLoad, true);
        return () => el.removeEventListener('load', onLoad, true);
    }, [autoScrollOnNew, isNearBottom, scrollToBottom]);

    useEffect(() => {
        const node = contentRef.current;
        if (!node) return;

        const ro = new ResizeObserver(() => {
            if (autoScrollOnNew === 'always' || isNearBottom()) {
                scrollToBottom(true);
            }
        });

        ro.observe(node);
        return () => ro.disconnect();
    }, [autoScrollOnNew, isNearBottom, scrollToBottom]);

    return (
        <section dir='rtl' className={cn('relative mx-auto h-full w-full rounded-3xl pb-10', className)}>
            <div
                ref={wrapperRef}
                className={cn(
                    'relative [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                    makeScrollable && 'max-h-[85vh] overflow-y-auto'
                )}
            >
                <div ref={contentRef} className='space-y-4 pb-24'>
                    {groups.map((g, groupIndex) => (
                        <div key={`${g.date.year}-${g.date.month}-${g.date.day}`}>
                            <ChipDate date={g.date} />
                            <div className='space-y-3'>
                                {g.items.map((it, itemIndex) => {
                                    const isFirstItem = groupIndex === 0 && itemIndex === 0;
                                    return isFirstItem ? (
                                        <TicketDescription key={it.id} item={it} />
                                    ) : (
                                        <ChatBubble key={it.id} item={it} />
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    <div ref={bottomRef} />
                </div>
            </div>

            <button
                type='button'
                onClick={() => scrollToBottom(true)}
                aria-label='رفتن به آخرین پیام'
                className={cn(
                    'pointer-events-auto fixed sm:absolute',
                    'end-4 bottom-[125px] lg:bottom-[45px]',
                    'rounded-md px-4 py-3 text-xs shadow-md',
                    'bg-primary-600 hover:bg-primary-700 text-white transition',
                    'focus:ring-primary-300 focus:ring-2 focus:outline-none',
                    !showGoDown && 'hidden'
                )}
            >
                <ChevronIcon className='-rotate-90' />
            </button>
        </section>
    );
};

export default TicketThread;

